'use client';

import { PlayerRef } from '@remotion/player';
import { LoadingSpinner } from '@crabs-ai/ds-react';
import { useTranslations } from 'next-intl';
import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  getGtsVideosApi,
  QueryKeyGetGtsVideos,
} from '@/features/gts/api/get-gts-videos';
import { GtsGalleryVideoItem } from '@/features/gts/model/gts-types';
import {
  DtoGtsVideoStatus,
  DtoGtsGenerationMode,
} from '@/shared/constants/dto';
import AiVideoGeneratedItem from './AiVideoGeneratedItem';

interface AiVideoGeneratedListProps {
  projectId: number;
  onVideoClick?: (video: GtsGalleryVideoItem, startFrame?: number) => void;
  playerRef: React.RefObject<PlayerRef | null>;
}

export default function AiVideoGeneratedList({
  projectId,
  onVideoClick,
  playerRef,
}: AiVideoGeneratedListProps) {
  const t = useTranslations();

  const { data, isLoading } = useQuery({
    queryKey: [QueryKeyGetGtsVideos, projectId],
    queryFn: () => getGtsVideosApi(projectId),
  });

  const allVideos = data?.data ?? [];
  // SHOWCASE 모드만 필터링
  const videos = allVideos
    .filter((v) => v.mode === DtoGtsGenerationMode.SHOWCASE)
    .sort((a, b) => {
      const updatedA = new Date(a.updatedAt ?? a.createdAt).getTime();
      const updatedB = new Date(b.updatedAt ?? b.createdAt).getTime();
      if (updatedA !== updatedB) {
        return updatedB - updatedA;
      }
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  const hasProcessingVideos = videos.some(
    (v) => v.status === DtoGtsVideoStatus.PROCESSING,
  );

  // processing 중인 영상이 있으면 5초 간격 자동 폴링 (같은 queryKey로 refetchInterval 설정)
  useQuery({
    queryKey: [QueryKeyGetGtsVideos, projectId],
    queryFn: () => getGtsVideosApi(projectId),
    enabled: hasProcessingVideos,
    refetchInterval: 5000,
  });

  // 생성 완료 시 자동으로 타임라인에 추가 (생성 시작 시점의 피커 위치 기준)
  const previousStatusesRef = useRef<Map<string, number>>(new Map());
  const processingFrameRef = useRef<Map<string, number>>(new Map());
  const isInitializedRef = useRef(false);

  useEffect(() => {
    if (!videos.length) return;

    if (isInitializedRef.current) {
      for (const video of videos) {
        const prevStatus = previousStatusesRef.current.get(video.id);
        if (
          prevStatus === DtoGtsVideoStatus.PROCESSING &&
          video.status === DtoGtsVideoStatus.COMPLETED
        ) {
          const startFrame = processingFrameRef.current.get(video.id);
          onVideoClick?.(video, startFrame);
          processingFrameRef.current.delete(video.id);
        }
      }
    }

    // PROCESSING 상태로 처음 감지된 비디오의 피커 위치 기록
    for (const video of videos) {
      if (
        video.status === DtoGtsVideoStatus.PROCESSING &&
        !processingFrameRef.current.has(video.id)
      ) {
        processingFrameRef.current.set(
          video.id,
          playerRef.current?.getCurrentFrame() ?? 0,
        );
      }
    }

    previousStatusesRef.current = new Map(videos.map((v) => [v.id, v.status]));
    isInitializedRef.current = true;
  }, [data, onVideoClick, playerRef]);

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (videos.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center px-md py-sm">
        <p className="text-sys-cont-neutral-tertiary text-body-small">
          {t('Editor.ai-video.no-generated-videos')}
        </p>
      </div>
    );
  }

  return (
    <div className="grid w-full grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-2xs">
      {videos.map((video) => (
        <AiVideoGeneratedItem
          key={video.id}
          video={video}
          projectId={projectId}
          onClick={onVideoClick}
        />
      ))}
    </div>
  );
}
